import React from 'react'
import { observer } from 'mobx-react-lite'
import { TextField } from '@mui/material'
import { DateTime } from 'luxon'
import { color } from '@trivie/ui-web/theme'
import { Text } from '@trivie/ui-web/components/text'
import { Switch } from '@trivie/ui-web/components/switch/switch'
import { IFormInputs } from '../assign-survey'

interface SurveyDetailsProps {
  fd: IFormInputs
  setFd: (fd: IFormInputs) => void
  touched: Record<string, boolean>
  setTouched: (touched: Record<string, boolean>) => void
  errors: Record<string, { type: string; message: string }>
}

export const SurveyDetails = observer((props: SurveyDetailsProps) => {
  const { fd, setFd, touched, setTouched, errors } = props

  const MinDate = DateTime.local().toFormat('yyyy-MM-dd')
  const MaxDate = DateTime.fromISO(fd.end_date).toFormat('yyyy-MM-dd')

  let startTimeMax = '23:59'
  if (
    DateTime.fromISO(fd.start_date).toFormat('yyyy-MM-dd') ===
    DateTime.fromISO(fd.end_date).toFormat('yyyy-MM-dd')
  ) {
    startTimeMax = DateTime.fromISO(`${fd.end_date}T${fd.end_time}:00`).toFormat('HH:mm')
  }

  let endTimeMin = '00:00'
  if (
    DateTime.fromISO(fd.start_date).toFormat('yyyy-MM-dd') ===
    DateTime.fromISO(fd.end_date).toFormat('yyyy-MM-dd')
  ) {
    endTimeMin = DateTime.fromISO(`${fd.start_date}T${fd.start_time}:00`).toFormat('HH:mm')
  }

  return (
    <>
      <div style={{ flexDirection: 'row', width: '100%', marginBottom: 32, marginTop: 12 }}>
        <TextField
          required
          fullWidth
          error={touched.title && !!errors.title}
          helperText={touched.title && errors.title?.message}
          label="Name"
          variant="outlined"
          placeholder="Name"
          onChange={(e) => {
            setFd({ ...fd, title: e.target.value })
            setTouched({ ...touched, title: true })
          }}
          onBlur={() => setTouched({ ...touched, title: true })}
          value={fd.title}
        />
      </div>
      <div style={DATE_CONTAINER}>
        <TextField
          error={!!errors.start_date}
          helperText={errors.start_date?.message}
          variant="outlined"
          label="Start Date"
          inputProps={{ min: MinDate, max: MaxDate }}
          size="small"
          style={{ width: '100%', marginRight: 8 }}
          type="date"
          onChange={(e) => {
            if (!e.target.value) return
            let _value = e.target.value
            if (_value < MinDate) _value = MinDate
            setFd({ ...fd, start_date: _value })
            setTouched({ ...touched, start_date: true })
          }}
          onBlur={() => setTouched({ ...touched, start_date: true })}
          value={fd.start_date}
        />
        <TextField
          error={!!errors.start_time}
          type="time"
          size="small"
          variant="outlined"
          inputProps={{ max: startTimeMax }}
          style={{ width: '100%', marginRight: 8 }}
          onChange={(e) => {
            setFd({ ...fd, start_time: e.target.value })
            setTouched({ ...touched, start_time: true })
          }}
          onBlur={() => setTouched({ ...touched, start_time: true })}
          value={fd.start_time}
        />
        <Text
          style={{ padding: 8, paddingRight: 0 }}
          variant="body1"
          text={Intl.DateTimeFormat().resolvedOptions().timeZone}
        />
      </div>

      <div style={DATE_CONTAINER}>
        <TextField
          disabled={fd.open_ended}
          error={touched.end_date && !!errors.end_date}
          helperText={touched.end_date && errors.end_date?.message}
          variant="outlined"
          label="End Date"
          inputProps={{ min: DateTime.fromISO(fd.start_date).toFormat('yyyy-MM-dd') }}
          size="small"
          style={{ width: '100%', marginRight: 8 }}
          type="date"
          onChange={(e) => {
            setTouched({ ...touched, end_date: true })
            if (!e.target.value) return
            setFd({ ...fd, end_date: e.target.value })
          }}
          onBlur={() => setTouched({ ...touched, end_date: true })}
          value={fd.end_date}
        />
        <TextField
          disabled={fd.open_ended}
          error={!!errors.end_time}
          type="time"
          size="small"
          variant="outlined"
          inputProps={{ min: endTimeMin }}
          style={{ width: '100%', marginRight: 8 }}
          onChange={(e) => {
            setFd({ ...fd, end_time: e.target.value })
            setTouched({ ...touched, end_time: true })
          }}
          onBlur={() => setTouched({ ...touched, end_time: true })}
          value={fd.end_time}
        />
        <Text
          style={{ padding: 8, paddingRight: 0 }}
          variant="body1"
          text={Intl.DateTimeFormat().resolvedOptions().timeZone}
        />
      </div>
      <div style={SWITCH_ROW}>
        <div style={SWITCH_TEXT}>
          <Text style={{ marginBottom: 8 }} variant="body1Medium" text="Open Ended" />
          <Text
            variant="body2Medium"
            text="Survey stays open until you end it manually"
            style={{ color: color.shade50, marginRight: 60 }}
          />
        </div>
        <Switch
          color="success"
          checked={fd.open_ended}
          onChange={() => setFd({ ...fd, open_ended: !fd.open_ended })}
          style={{ paddingRight: 30 }}
        />
      </div>
      <div style={SWITCH_CONTAINER}>
        <div style={SWITCH_ROW}>
          <div style={SWITCH_TEXT}>
            <Text style={{ marginBottom: 8 }} variant="body1Medium" text="Notify Learners" />
            <Text
              variant="body2Medium"
              text="Send learners a notification when they receive the survey"
              style={{ color: color.shade50, marginRight: 60 }}
            />
          </div>
          <Switch
            color="success"
            checked={fd.notify}
            onChange={() => setFd({ ...fd, notify: !fd.notify })}
            style={{ paddingRight: 30 }}
          />
        </div>
        <div style={SWITCH_ROW}>
          <div style={SWITCH_TEXT}>
            <Text style={{ marginBottom: 8 }} variant="body1Medium" text="Anonymize Responses" />
            <Text
              variant="body2Medium"
              text="Hide names from server responses for administrators"
              style={{ color: color.shade50, marginRight: 60 }}
            />
          </div>
          <Switch
            color="success"
            checked={fd.is_anonymous}
            onChange={() => setFd({ ...fd, is_anonymous: !fd.is_anonymous })}
          />
        </div>
      </div>
    </>
  )
})

const DATE_CONTAINER: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
  width: '100%',
  marginBottom: 20,
}

const SWITCH_CONTAINER: React.CSSProperties = {
  justifyContent: 'space-between',
  flexDirection: 'row',
  width: '100%',
  paddingTop: 20,
}

const SWITCH_ROW: React.CSSProperties = {
  display: 'flex',
  marginBottom: 22,
  alignItems: 'center',
  justifyContent: 'space-between',
}

const SWITCH_TEXT: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
}
